import React, { useState, useEffect, useCallback } from 'react';
import { dataService } from '../services/dataService';
import { getAllWorkflows, clearWorkflowState } from '../utils/workflowEngine';
import { useWorkspaceRole } from '../contexts/WorkspaceRoleContext';

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATUS_META = {
  running:   { label: 'Running',   color: '#6366f1', bg: '#eef2ff', icon: '▶' },
  paused:    { label: 'Paused',    color: '#f59e0b', bg: '#fef3c7', icon: '⏸' },
  completed: { label: 'Completed', color: '#10b981', bg: '#d1fae5', icon: '✓' },
  failed:    { label: 'Failed',    color: '#f43f5e', bg: '#fee2e2', icon: '✕' },
};

const NODE_TYPE_ICON = {
  triggerNode:   '⚡', conditionNode: '◇', actionNode: '⚙',
  functionNode:  '⬡', humanNode:     '👤', gateNode:  '◆',
  collectionNode:'⊞', noteNode:      '📝', connectionNode: '🔌',
};

const RECORD_STATUS_COLORS = {
  'Open': '#3b82f6', 'In Progress': '#f59e0b', 'Resolved': '#10b981',
  'Done': '#10b981', 'Escalated': '#f43f5e', 'Backlog': '#94a3b8', 'Review': '#8b5cf6',
};
const PRIORITY_COLORS = { 'Critical': '#f43f5e', 'High': '#f97316', 'Medium': '#f59e0b', 'Low': '#22c55e' };

function fmtTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function fmtDuration(startIso, endIso) {
  if (!startIso) return null;
  const ms = (endIso ? new Date(endIso) : new Date()) - new Date(startIso);
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
}

function StatusChip({ status }) {
  const m = STATUS_META[status] || { label: status, color: '#94a3b8', bg: '#f1f5f9', icon: '?' };
  return (
    <span className="wf-status-chip" style={{ background: m.bg, color: m.color }}>
      {m.icon} {m.label}
    </span>
  );
}

// ── Workflow detail panel ─────────────────────────────────────────────────────
function WorkflowDetail({ wf, onClose, onDelete }) {
  const steps = wf.steps || [];
  return (
    <div className="wf-detail">
      <div className="wf-detail-hdr">
        <div className="wf-detail-title">
          <StatusChip status={wf.status} />
          <span className="wf-detail-id">{wf.id}</span>
        </div>
        <button className="wf-detail-close" aria-label="Close dialog" title="Close" onClick={onClose}>×</button>
      </div>

      <div className="wf-detail-meta">
        <div className="wf-meta-row"><span>Trigger</span><strong>{wf.triggerLabel || wf.triggerNodeId}</strong></div>
        {wf.recordId && <div className="wf-meta-row"><span>Record</span><strong>{wf.recordId}</strong></div>}
        <div className="wf-meta-row"><span>Started</span><strong>{fmtTime(wf.startedAt)}</strong></div>
        {wf.completedAt && <div className="wf-meta-row"><span>Completed</span><strong>{fmtTime(wf.completedAt)}</strong></div>}
        <div className="wf-meta-row"><span>Duration</span><strong>{fmtDuration(wf.startedAt, wf.completedAt || wf.pausedAt)}</strong></div>
        {wf.pauseReason && <div className="wf-meta-row"><span>Paused at</span><strong>{wf.pauseReason}</strong></div>}
      </div>

      <div className="wf-steps-title">Steps ({steps.length})</div>
      {steps.length === 0 && <div className="wf-steps-empty">No steps recorded.</div>}
      <div className="wf-steps">
        {steps.map((step, i) => {
          const m = STATUS_META[step.status] || STATUS_META.completed;
          return (
            <div key={`${step.nodeId}-${i}`} className="wf-step">
              <div className="wf-step-indicator" style={{ background: m.bg, color: m.color }}>
                {NODE_TYPE_ICON[step.nodeType] || '◉'}
              </div>
              <div className="wf-step-body">
                <div className="wf-step-label">{step.nodeLabel || step.nodeType}</div>
                {step.detail && <div className="wf-step-detail">{step.detail}</div>}
                <div className="wf-step-time">{fmtTime(step.startedAt)}{step.completedAt ? ` · ${fmtDuration(step.startedAt, step.completedAt)}` : ''}</div>
              </div>
              <span className="wf-step-status" style={{ color: m.color }}>{m.icon}</span>
            </div>
          );
        })}
      </div>

      <button className="wf-delete-btn" onClick={() => onDelete(wf.id)}>Delete run</button>
    </div>
  );
}

// ── Workflow list ─────────────────────────────────────────────────────────────
function WorkflowsTab() {
  const [workflows,  setWorkflows]  = useState([]);
  const [selected,   setSelected]   = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');

  const refresh = useCallback(() => {
    setWorkflows(getAllWorkflows());
  }, []);

  useEffect(() => {
    refresh();
    const onUpdate = () => refresh();
    window.addEventListener('projectly_workflow_update', onUpdate);
    return () => window.removeEventListener('projectly_workflow_update', onUpdate);
  }, [refresh]);

  const handleDelete = (id) => {
    clearWorkflowState(id);
    setWorkflows(getAllWorkflows());
    if (selected?.id === id) setSelected(null);
  };

  const filtered = filterStatus === 'all' ? workflows : workflows.filter(w => w.status === filterStatus);
  const counts   = { running: 0, paused: 0, completed: 0, failed: 0 };
  workflows.forEach(w => { if (counts[w.status] !== undefined) counts[w.status]++; });

  return (
    <div className="wf-tab">
      {/* Summary pills */}
      <div className="wf-summary-row">
        {Object.entries(STATUS_META).map(([key, m]) => (
          <button
            key={key}
            className={`wf-summary-pill${filterStatus === key ? ' active' : ''}`}
            style={{ '--pill-color': m.color, '--pill-bg': m.bg }}
            onClick={() => setFilterStatus(f => f === key ? 'all' : key)}
          >
            <span className="wf-pill-icon">{m.icon}</span>
            <span className="wf-pill-label">{m.label}</span>
            <span className="wf-pill-count">{counts[key]}</span>
          </button>
        ))}
        <button
          className={`wf-summary-pill${filterStatus === 'all' ? ' active' : ''}`}
          style={{ '--pill-color': '#64748b', '--pill-bg': '#f1f5f9' }}
          onClick={() => setFilterStatus('all')}
        >
          <span className="wf-pill-label">All</span>
          <span className="wf-pill-count">{workflows.length}</span>
        </button>
      </div>

      <div className={`wf-body${selected ? ' wf-split' : ''}`}>
        {/* List */}
        <div className="wf-list">
          {filtered.length === 0 && (
            <div className="wf-empty">
              <div className="wf-empty-icon">▶</div>
              <div className="wf-empty-title">No workflow runs yet</div>
              <div className="wf-empty-desc">Run a Trigger node on your pipeline canvas to start tracking workflow executions here.</div>
            </div>
          )}
          {filtered.map(wf => {
            const m = STATUS_META[wf.status] || STATUS_META.completed;
            const stepCount = (wf.steps || []).length;
            const isActive = selected?.id === wf.id;
            return (
              <div
                key={wf.id}
                className={`wf-row${isActive ? ' selected' : ''}`}
                onClick={() => setSelected(s => s?.id === wf.id ? null : wf)}
              >
                <div className="wf-row-left">
                  <div className="wf-row-status-dot" style={{ background: m.color }} title={m.label} />
                  <div className="wf-row-info">
                    <div className="wf-row-trigger">{wf.triggerLabel || wf.triggerNodeId}</div>
                    <div className="wf-row-meta">
                      {fmtTime(wf.startedAt)}
                      {wf.recordId && <span className="wf-row-record"> · record {wf.recordId}</span>}
                      {stepCount > 0 && <span> · {stepCount} step{stepCount !== 1 ? 's' : ''}</span>}
                    </div>
                  </div>
                </div>
                <div className="wf-row-right">
                  <StatusChip status={wf.status} />
                  <span className="wf-row-dur">{fmtDuration(wf.startedAt, wf.completedAt || wf.pausedAt)}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Detail panel */}
        {selected && (
          <WorkflowDetail
            wf={selected}
            onClose={() => setSelected(null)}
            onDelete={handleDelete}
          />
        )}
      </div>
    </div>
  );
}

// ── Records tab (preserved from old TasksView) ────────────────────────────────
function RecordsTab({ workspaceId }) {
  const { hasCapability } = useWorkspaceRole();
  const canEditRecords = hasCapability('change_status');
  const [nodes,        setNodes]        = useState([]);
  const [recordsByCol, setRecordsByCol] = useState({});
  const [loading,      setLoading]      = useState(true);
  const [filterCol,    setFilterCol]    = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showCreate,   setShowCreate]   = useState(false);
  const [newTask,      setNewTask]      = useState({ collection: '', title: '', status: 'Open', priority: 'Medium' });

  useEffect(() => {
    if (!workspaceId) return;
    dataService.getPipeline(workspaceId).then(data => {
      setNodes(data.nodes || []);
      setLoading(false);
    });
  }, [workspaceId]);

  const collections = nodes.filter(n => n.type === 'collectionNode');

  useEffect(() => {
    if (!workspaceId || !collections.length) return;
    const unsubs = collections.map(col =>
      dataService.subscribeRecords(workspaceId, col.id, (recs) => {
        setRecordsByCol(prev => ({ ...prev, [col.id]: recs }));
      })
    );
    return () => unsubs.forEach(u => u && u());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, collections.map(c => c.id).join(',')]);

  const allRecords = collections.flatMap(col => {
    const recs = recordsByCol[col.id] || [];
    const fields = col.data.fields || [];
    const titleField    = fields.find(f => f.type === 'text');
    const statusField   = fields.find(f => f.name.toLowerCase().includes('status'));
    const priorityField = fields.find(f => f.name.toLowerCase().includes('priority'));
    const agentField    = fields.find(f => f.type === 'agent');
    return recs.map(r => ({
      id: r.id,
      title:    titleField    ? (r.values?.[titleField.id]    || '—') : r.id,
      status:   statusField   ? (r.values?.[statusField.id]   || null) : null,
      priority: priorityField ? (r.values?.[priorityField.id] || null) : null,
      assignee: agentField    ? (r.values?.[agentField.id]    || null) : null,
      collectionId:   col.id,
      collectionName: col.data.label,
    }));
  });

  const statuses = [...new Set(allRecords.map(t => t.status).filter(Boolean))];
  const filtered = allRecords.filter(t =>
    (filterCol    === 'all' || t.collectionId === filterCol) &&
    (filterStatus === 'all' || t.status       === filterStatus)
  );

  const addRecord = () => {
    if (!newTask.collection || !newTask.title.trim()) return;
    const col = nodes.find(n => n.id === newTask.collection);
    if (!col) return;
    const fields    = col.data.fields || [];
    const titleF    = fields.find(f => f.type === 'text');
    const statusF   = fields.find(f => f.name.toLowerCase().includes('status'));
    const priorityF = fields.find(f => f.name.toLowerCase().includes('priority'));
    const values = {};
    if (titleF)    values[titleF.id]    = newTask.title;
    if (statusF)   values[statusF.id]   = newTask.status;
    if (priorityF) values[priorityF.id] = newTask.priority;
    dataService.addRecord(workspaceId, newTask.collection, values);
    setNewTask({ collection: '', title: '', status: 'Open', priority: 'Medium' });
    setShowCreate(false);
  };

  if (loading) return <div className="pipeline-loading">Loading records…</div>;

  return (
    <div className="records-tab">
      <div className="records-tab-hdr">
        <div className="records-tab-filters">
          <select className="task-filter-select" value={filterCol} onChange={e => setFilterCol(e.target.value)}>
            <option value="all">All collections</option>
            {collections.map(c => <option key={c.id} value={c.id}>{c.data.label}</option>)}
          </select>
          <select className="task-filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="all">All statuses</option>
            {statuses.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
        {canEditRecords && (
          <button className="tasks-create-btn" onClick={() => setShowCreate(s => !s)}>
            {showCreate ? '× Cancel' : '+ Add record'}
          </button>
        )}
      </div>

      {showCreate && (
        <div className="tasks-create-form">
          <select className="panel-input" value={newTask.collection}
            onChange={e => setNewTask(p => ({ ...p, collection: e.target.value }))}>
            <option value="">Select collection…</option>
            {collections.map(c => <option key={c.id} value={c.id}>{c.data.label}</option>)}
          </select>
          <input className="panel-input" placeholder="Title / description"
            value={newTask.title} onChange={e => setNewTask(p => ({ ...p, title: e.target.value }))} />
          <select className="panel-input" value={newTask.status}
            onChange={e => setNewTask(p => ({ ...p, status: e.target.value }))}>
            {['Open','In Progress','Resolved','Escalated','Backlog','Review','Done'].map(s => <option key={s}>{s}</option>)}
          </select>
          <select className="panel-input" value={newTask.priority}
            onChange={e => setNewTask(p => ({ ...p, priority: e.target.value }))}>
            {['Low','Medium','High','Critical'].map(p => <option key={p}>{p}</option>)}
          </select>
          <button className="panel-add-agent-btn" onClick={addRecord}>Add record</button>
        </div>
      )}

      {collections.length === 0 ? (
        <div className="tasks-empty">
          <div className="tasks-empty-icon">⊞</div>
          <p>No collections in your pipeline yet.</p>
          <p className="tasks-empty-sub">Add a Collection node to your pipeline to see records here.</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="tasks-empty"><p>No records match your filters.</p></div>
      ) : (
        <div className="tasks-list">
          {filtered.map(rec => (
            <div key={`${rec.collectionId}-${rec.id}`} className="task-row">
              <div className="task-row-left">
                <span className="task-collection-dot" />
                <div className="task-row-info">
                  <span className="task-row-title">{rec.title}</span>
                  <span className="task-row-collection">{rec.collectionName}</span>
                </div>
              </div>
              <div className="task-row-right">
                {rec.assignee && <span className="task-assignee">→ {rec.assignee}</span>}
                {rec.priority && (
                  <span className="task-badge" style={{ background: (PRIORITY_COLORS[rec.priority] || '#94a3b8') + '22', color: PRIORITY_COLORS[rec.priority] || '#94a3b8' }}>
                    {rec.priority}
                  </span>
                )}
                {rec.status && (
                  <span className="task-badge" style={{ background: (RECORD_STATUS_COLORS[rec.status] || '#94a3b8') + '22', color: RECORD_STATUS_COLORS[rec.status] || '#94a3b8' }}>
                    {rec.status}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main TasksView ────────────────────────────────────────────────────────────
export default function TasksView({ workspaceId }) {
  const [tab, setTab] = useState('workflows');
  const [runningCount, setRunningCount] = useState(() =>
    getAllWorkflows().filter(w => w.status === 'running' || w.status === 'paused').length
  );

  useEffect(() => {
    const onUpdate = () => {
      setRunningCount(getAllWorkflows().filter(w => w.status === 'running' || w.status === 'paused').length);
    };
    window.addEventListener('projectly_workflow_update', onUpdate);
    return () => window.removeEventListener('projectly_workflow_update', onUpdate);
  }, []);

  return (
    <div className="tasks-view">
      <div className="tasks-header">
        <div className="tasks-header-left">
          <h2 className="tasks-title">Tasks</h2>
        </div>
        <div className="tasks-tab-bar">
          <button className={`tasks-tab-btn${tab === 'workflows' ? ' active' : ''}`} onClick={() => setTab('workflows')}>
            Workflow Runs
            {runningCount > 0 && <span className="tasks-tab-badge">{runningCount}</span>}
          </button>
          <button className={`tasks-tab-btn${tab === 'records' ? ' active' : ''}`} onClick={() => setTab('records')}>
            Collection Records
          </button>
        </div>
      </div>

      {tab === 'workflows' ? <WorkflowsTab /> : <RecordsTab workspaceId={workspaceId} />}
    </div>
  );
}
